import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";

import { Button } from "../../components/core/basic/Button";
import { RT8ColumnSelector } from "../../components/core/blocks/MetricSelector";
import { createAppColumnHelper, useClientTable } from "../../components/table/tableFeatures";
import { usePersistedTableState } from "../../components/table/usePersistedTableState";
import { customFilterFn } from "../../components/table/filterFns";
import { resetTable } from "../../components/table/utils";
import { RT8Pagination } from "../../components/table/Pagination";
import { Table } from "../../components/table/Table";
import { RT8FilterWidget } from "../merchandising/components/tables/FilterWidget";
import { useAppSelector } from "../../redux/hooks";
import { formatCurrency, formatNumber, formatPercent } from "../../utils/formatNumber";
import { css } from "../../../styled-system/css";
import { HStack, VStack } from "../../../styled-system/jsx";
import { useSimpleCan } from "../../config/casl/casl";

const columnHelper = createAppColumnHelper();

const AdDetails = ({ optData }) => {
  const { t } = useTranslation(["pricingv2", "common"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const userCanExportPricingPlans = useSimpleCan("export", "PricingPlan");

  const tableData = useMemo(() => {
    const sourceData = optData?.pricing_data?.filter((r) => r.num_days_at_price > 0) ?? [];
    return sourceData.map((row) => {
      const { impressions, num_days_at_price, clicks, conversions, sales, cost } = row;
      const total_sales = row.price * row.avg_daily_sales * num_days_at_price;

      return {
        ...row,
        total_sales,
        impressions_per_day: num_days_at_price ? impressions / num_days_at_price : 0,
        clicks_per_day: num_days_at_price ? clicks / num_days_at_price : 0,
        conversions_per_day: num_days_at_price ? conversions / num_days_at_price : 0,
        ctr_calculated: impressions ? clicks / impressions : 0,
        cr_calculated: clicks ? conversions / clicks : 0,

        sales_per_day: sales / num_days_at_price,
        cost_per_day: num_days_at_price ? cost / num_days_at_price : 0,
        acos_calculated: sales ? cost / sales : 0,
        tacos_calculated: total_sales ? cost / total_sales : 0,
      };
    });
  }, [optData]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("price", {
        header: t(($) => $.pricingv2["Price"]),
        size: 100,
        enableHiding: false,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("num_days_at_price", {
        header: t(($) => $.pricingv2["Days at Price"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatNumber(getValue(), { decimalPlaces: 1 }),
        meta: { justify: "end", columnFilterOptions: { format: "number" } },
      }),
      columnHelper.accessor("avg_daily_sales", {
        header: t(($) => $.pricingv2["Daily Demand"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatNumber(getValue()),
        meta: { justify: "end", columnFilterOptions: { format: "number" } },
      }),
      columnHelper.accessor("impressions_per_day", {
        header: t(($) => $.pricingv2["Imp/Day"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatNumber(getValue(), { decimalPlaces: 0 }),
        meta: { justify: "end", columnFilterOptions: { format: "number" } },
      }),
      columnHelper.accessor("clicks_per_day", {
        header: t(($) => $.pricingv2["Clicks/Day"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatNumber(getValue(), { decimalPlaces: 0 }),
        meta: { justify: "end", columnFilterOptions: { format: "number" } },
      }),
      columnHelper.accessor("conversions_per_day", {
        header: t(($) => $.pricingv2["Conv/Day"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatNumber(getValue()),
        meta: { justify: "end", columnFilterOptions: { format: "number" } },
      }),
      columnHelper.accessor("ctr_calculated", {
        header: t(($) => $.pricingv2["CTR"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatPercent(getValue()),
        meta: { justify: "end", columnFilterOptions: { format: "percent" } },
      }),
      columnHelper.accessor("cr_calculated", {
        header: t(($) => $.pricingv2["CR"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatPercent(getValue()),
        meta: { justify: "end", columnFilterOptions: { format: "percent" } },
      }),
      columnHelper.accessor("sales_per_day", {
        header: t(($) => $.pricingv2["Ad Sales/Day"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("cost_per_day", {
        header: t(($) => $.pricingv2["Cost/Day"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("acos_calculated", {
        header: t(($) => $.pricingv2["ACOS"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatPercent(getValue()),
        meta: { justify: "end", columnFilterOptions: { format: "percent" } },
      }),
    ],
    [t, marketPlace],
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "priceAdDetailsTable",
    initialState: { sorting: [{ id: "price", desc: false }] },
  });

  const table = useClientTable({
    data: tableData,
    columns,
    defaultColumn: {
      filterFn: customFilterFn,
    },
    initialState,
    state,
    ...setters,
  });

  const downloadCSV = () => {
    const data = [
      [
        "price",
        "day_at_price",
        "daily_demand",
        "impressions/day",
        "clicks/day",
        "conversions/day",
        "CTR",
        "CR",
        "sales/day",
        "cost/day",
        "ACOS",
      ],
    ];

    tableData.forEach((row) => {
      data.push([
        row.price,
        row.num_days_at_price ? row.num_days_at_price.toFixed(2) : 0,
        row.avg_daily_sales ? row.avg_daily_sales.toFixed(2) : 0,
        row.impressions_per_day ? row.impressions_per_day.toFixed(2) : 0,
        row.clicks_per_day ? row.clicks_per_day.toFixed(2) : 0,
        row.conversions_per_day ? row.conversions_per_day.toFixed(2) : 0,
        row.ctr_calculated ? row.ctr_calculated.toFixed(2) : 0,
        row.cr_calculated ? row.cr_calculated.toFixed(4) : 0,
        row.sales_per_day ? row.sales_per_day.toFixed(2) : 0,
        row.cost_per_day ? row.cost_per_day.toFixed(2) : 0,
        row.acos_calculated ? row.acos_calculated.toFixed(2) : 0,
        row.tacos_calculated ? row.tacos_calculated.toFixed(2) : 0,
      ]);
    });

    const csvContent = data.map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "advertising_effect_download.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              aria-label={t(($) => $.common.table["Reset table filters"])}
              onPress={() => resetTable(table)}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
      {userCanExportPricingPlans && (
        <Button
          onPress={() => {
            downloadCSV();
          }}
          className={css({ marginLeft: "5px" })}
        >
          {t(($) => $.pricingv2["Download CSV"])}
        </Button>
      )}
    </>
  );
};

export default AdDetails;
